import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import classnames from 'classnames/bind';
import { Fade } from 'react-awesome-reveal';
import { Logo } from '../Logo';
import { Button } from '../Button';
import { Icon } from '../Icon';
import CustomLink from '../CustomLink';
import { useAuthentication } from '../../shared/use-authentication';
import { useUser } from '../../shared/use-user';
import { useRouting } from '../../shared/use-routing';
import { pagePathnames } from '../../pages/page-routes';
import styles from './Header.css';

const cx = classnames.bind(styles);

function Header({className, isAuthenticated}) {
    const {location} = useRouting();
    const {userData, isLoading} = useUser();
    const {signOut} = useAuthentication();
    const classes = cx({
        root: true,
        [className]: Boolean(className),
        authenticated: isAuthenticated
    });
    const withPremiumLink = !isLoading && !userData.isActiveSubscription;
    const [isMobileMenu, setIsMobileMenu] = useState(false);

    const openMobileMenu = () => {
        setIsMobileMenu(true);
    };

    const closeMobileMenu = () => {
        setIsMobileMenu(false);
    };

    useEffect(() => {
        if (isMobileMenu) {
            closeMobileMenu();
        }
    }, [location.pathname]);

    const renderLinks = position => {
        if (position === 'left') {
            return (
                <>
                    <CustomLink
                        to={pagePathnames.main}
                        className={cx('link', {active: location.pathname === pagePathnames.main})}
                    >
                        Main
                    </CustomLink>
                    <CustomLink
                        to={pagePathnames.aboutUs}
                        className={cx('link', {active: location.pathname === pagePathnames.aboutUs})}
                    >
                        About us
                    </CustomLink>
                </>
            )
        }

        if (position === 'right') {
            return (
                <>
                    {withPremiumLink && (
                        <CustomLink
                            to={pagePathnames.premium}
                            className={cx('link', {active: location.pathname === pagePathnames.premium})}
                        >
                            Premium subscription
                        </CustomLink>
                    )}
                    <CustomLink
                        to={pagePathnames.profileSettings}
                        className={cx('link', {active: location.pathname === pagePathnames.profileSettings})}
                    >
                        Account settings
                    </CustomLink>
                </>
            )
        }
    };

    if (isAuthenticated) {
        return (
            <header className={classes}>
                <div className={styles.wrapper}>
                    {isMobileMenu
                        ? (
                            <Button
                                type='secondary'
                                iconLeft={{name: 'close'}}
                                className={styles.iconClose}
                                onClick={closeMobileMenu}
                                rounded
                            />
                        )
                        : (
                            <Icon
                                name='menu'
                                className={styles.iconMenu}
                                onClick={openMobileMenu}
                            />
                        )
                    }
                    <nav className={styles.leftNav}>
                        <Logo url='/main' />
                        {renderLinks('left')}
                    </nav>
                    <nav className={styles.rightNav}>
                        {renderLinks('right')}
                        <Button
                            type='secondary'
                            iconLeft={{name: 'logout'}}
                            className={styles.iconClose}
                            onClick={signOut}
                            rounded
                        />
                    </nav>
                </div>
                {isMobileMenu && (
                    createPortal(
                        <Fade
                            className={styles.mobileMenuModal}
                            duration={200}
                            triggerOnce
                        >
                            <div className={styles.links}>
                                {renderLinks('left')}
                                {renderLinks('right')}
                            </div>
                        </Fade>,
                        document.body
                    )
                )}
            </header>
        );
    }

    return (
        <header className={classes}>
            <div className={styles.wrapper}>
                <div className={styles.subBlock} />
                <Logo />
                {location.pathname === pagePathnames.start
                    ? (
                        <Button
                            type='secondary'
                            iconLeft={{name: 'login'}}
                            className={styles.iconLogin}
                            link={{
                                to: pagePathnames.signIn
                            }}
                            rounded
                        />
                    )
                    : <div className={styles.subBlock} />
                }
            </div>
        </header>
    );
};

Header.propTypes = {
    className: PropTypes.string,
    isAuthenticated: PropTypes.bool
};

Header.defaultProps = {
    className: '',
    isAuthenticated: false
};

export {
    Header
};
